import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Form, Spinner } from "react-bootstrap";
import { FiMessageSquare, FiSend } from "react-icons/fi";

import userIcon from "../../../static/user_icon.png";
import { getApiUrl } from "../../../utils/apiUrl";
import { getCsrfToken } from "../../../utils/getCsrfToken";

const CommentAuthor = ({ author }) => {
  if (!author) return null;

  return (
    <img
      className="comment-author-avatar"
      src={author.image || userIcon}
      alt={author.name || "Usuario"}
      title={author.name || author.username || "Usuario"}
    />
  );
};

const CommentsSection = ({
  actividadId,
  detailPath = "api/perfiles/actividades",
  detailAction = "anotaciones",
  collectionPath = "api/perfiles/anotaciones/",
  foreignKey = "actividad",
  textField = "a_text",
}) => {
  const [newComment, setNewComment] = useState("");
  const [comments, setComments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  const fetchComments = useCallback(async () => {
    setIsLoading(true);
    setError("");
    try {
      const response = await fetch(
        getApiUrl(`${detailPath}/${actividadId}/${detailAction}/`),
        { credentials: "include" },
      );
      if (!response.ok) throw new Error("No se pudieron cargar los comentarios.");
      const data = await response.json();
      setComments(Array.isArray(data) ? data : []);
    } catch (requestError) {
      setComments([]);
      setError(requestError.message);
    } finally {
      setIsLoading(false);
    }
  }, [actividadId, detailAction, detailPath]);

  useEffect(() => { fetchComments(); }, [fetchComments]);

  const handleCommentSubmit = async () => {
    if (!newComment.trim()) {
      setError("Escribe un comentario antes de guardarlo.");
      return;
    }

    setIsSaving(true);
    setError("");
    try {
      const csrfToken = await getCsrfToken();
      const response = await fetch(getApiUrl(collectionPath), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
        body: JSON.stringify({
          [foreignKey]: actividadId,
          [textField]: newComment.trim(),
        }),
        credentials: "include",
      });
      if (!response.ok) throw new Error("No se pudo guardar el comentario.");
      setNewComment("");
      await fetchComments();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="actividad-info initiative-comments-section">
      <div className="initiative-section-heading initiative-tab-heading">
        <div><span>SEGUIMIENTO</span><h5>Comentarios</h5><p>{comments.length} comentarios guardados</p></div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <section className="initiative-tab-form-card">
        <Form.Group>
          <Form.Label>Nuevo comentario</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            placeholder="Añadir un comentario…"
            value={newComment}
            onChange={(event) => setNewComment(event.target.value)}
            disabled={isSaving}
          />
        </Form.Group>
        <Button
          type="button"
          className="initiative-tab-primary"
          onClick={handleCommentSubmit}
          disabled={!newComment.trim() || isSaving}
        >
          {isSaving ? <Spinner animation="border" size="sm" /> : <FiSend />}
          Guardar comentario
        </Button>
      </section>

      {isLoading ? (
        <div className="initiative-attendance-loading"><Spinner animation="border" size="sm" /> Cargando comentarios…</div>
      ) : comments.length > 0 ? (
        <div className="initiative-comments-list">
          {comments.map((comment) => (
            <article key={comment.id} className="actividad-comentario">
              <p>{comment[textField]}</p>
              <div className="comment-meta-row">
                <CommentAuthor author={comment.created_by_user} />
                <div>
                  <strong>{comment.created_by_user?.name || comment.created_by_user?.username || "Usuario"}</strong>
                  <span>{comment.date_observed}</span>
                </div>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <div className="initiative-tab-empty"><FiMessageSquare /><strong>Sin comentarios</strong><span>Todavía no hay comentarios guardados.</span></div>
      )}
    </div>
  );
};

export default CommentsSection;
